import { memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { OverlayTrigger, Tooltip, Spinner } from "react-bootstrap";

import CompleteTableData from "../../../../../../app/common-components/Table/CompleteTableData";
import Spinners from "../../../../../../app/common-components/Spinner";

import { getSortingValues } from "../../../../../../app/Constants";
import { calculatePageCount, useCommonAlert } from "../../../../../../app/utilities/helpers";

import {
  GetLatestExclusionStatus,
  VulnerabilityExclusionList,
  VulnerabilityExclusionToggleAction,
} from "../../ToolsSlice";

import "./styles/VulnerabilityManagementExclusion.module.scss";

const DEFAULT_PAGE = 0;
const DEFAULT_LIMIT = 10;

const VulnerabilityManagementExclusion = () => {
  const dispatch = useDispatch();
  const { callCommonAlert, hideCommonAlert } = useCommonAlert();

  const {
    VulnerabilityExclusionListLoading,
    VulnerabilityExclusionListData,
    VulnerabilityExclusionToggleActionLoading,
    VulnerabilityExclusionStatusData,
    VulnerabilityExclusionStatusLoading,
  } = useSelector((state) => state.toolsApi);

  const [isExclusionEnabled, setIsExclusionEnabled] = useState(false);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const loadExclusionList = (options = {}) => {
    const page = options.page ?? currentPage;
    const limit = options.limit ?? rowsPerPage;
    const keyword = options.keyword ?? searchKeyword;
    const sortBy = options.sortBy ?? sortColumn;
    const order = options.order ?? sortDirection;

    dispatch(
      VulnerabilityExclusionList({
        pageNumber: page,
        itemsPerPage: limit,
        payload: {
          keyword,
          sortByColumn: sortBy,
          sortOrder: order,
        },
      })
    ).then((result) => {
      if (VulnerabilityExclusionList.fulfilled.match(result)) {
        setCurrentPage(page);
        setRowsPerPage(limit);
        setSearchKeyword(keyword);
        setSortColumn(sortBy);
        setSortDirection(order);
      }
    });
  };

  // Fetch the latest exclusion status and table data on mount
  useEffect(() => {
    dispatch(GetLatestExclusionStatus());
    loadExclusionList({
      page: DEFAULT_PAGE,
      limit: DEFAULT_LIMIT,
    });
  }, []);

  // Sync local toggle state from the status API response
  useEffect(() => {
    if (VulnerabilityExclusionStatusData?.status !== undefined) {
      setIsExclusionEnabled(Boolean(VulnerabilityExclusionStatusData.status));
    }
  }, [VulnerabilityExclusionStatusData]);

  const handleToggleChange = () => {
    if (VulnerabilityExclusionToggleActionLoading) return;

    const newToggleState = !isExclusionEnabled;

    dispatch(
      VulnerabilityExclusionToggleAction({
        enableOrDisable: newToggleState,
      })
    ).then((result) => {
      if (VulnerabilityExclusionToggleAction.fulfilled.match(result)) {
        setIsExclusionEnabled(newToggleState);
        callCommonAlert(
          "success",
          `Vulnerability Exclusion ${
            newToggleState ? "Enabled" : "Disabled"
          } Successfully`
        );
      } else {
        const errorMessage = result.payload?.message || 
                            result.error?.message || 
                            "Failed to update exclusion setting.";
        
        console.error("Vulnerability Exclusion Toggle Error:", {
          payload: result.payload,
          error: result.error,
          errorMessage
        });
        
        callCommonAlert("error", errorMessage);
      }

      setTimeout(() => hideCommonAlert(), 3000);
    });
  };

  const tableColumns = useMemo(
    () => [
      {
        Header: "Exclusion Type",
        accessor: "exclusionType",
        id: "exclusionType",
        tdClassName: "text-left",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Description",
        accessor: "description",
        id: "description",
        tdClassName: "text-left",
        Cell: ({ value }) => value || "-",
      },
    ],
    []
  );

  const handlePageChange = (page) => {
    loadExclusionList({ page });
  };

  const handleLimitChange = (limit) => {
    loadExclusionList({
      page: DEFAULT_PAGE,
      limit,
    });
  };

  const handleSearch = (keyword) => {
    loadExclusionList({
      page: DEFAULT_PAGE,
      keyword,
    });
  };

  const handleSorting = (column, headerSortingState) => {
    const { sortingField, isDataSorted } = getSortingValues(
      tableColumns,
      column,
      headerSortingState
    );

    loadExclusionList({
      sortBy: sortingField,
      order: isDataSorted,
    });
  }

  const actionButtons = [
    {
      btnComponent: (
        <div className="d-flex align-items-center me-2 exclusion-toggle-container">
          <span className="me-2 fw-bold exclusion-label">
            Apply Exclusion:
          </span>
          {VulnerabilityExclusionToggleActionLoading ? (
            <OverlayTrigger 
              placement="top" 
              overlay={
                <Tooltip id="loading-tooltip">
                  {isExclusionEnabled 
                    ? "Disabling vulnerability exclusion. This may take a moment..." 
                    : "Enabling vulnerability exclusion. This may take a moment..."
                  }
                </Tooltip>
              }
              show={true}
            >
              <div className="d-flex align-items-center">
                <div className="toggle-spinner-container">
                  <Spinner animation="border" as="span" style={{ color: 'var(--primary-color)' }} size="sm" />
                </div>
                <span className="loading-text">
                  {isExclusionEnabled ? "Disabling..." : "Enabling..."}
                </span>
              </div>
            </OverlayTrigger>
          ) : !isExclusionEnabled ? (
            <OverlayTrigger 
              placement="top" 
              overlay={
                <Tooltip id="disabled-tooltip">
                  When enabled, matching vulnerabilities will be excluded from findings.
                </Tooltip>
              }
            >
              <label className="toggle-control mb-0">
                <input
                  type="checkbox"
                  checked={isExclusionEnabled}
                  onChange={handleToggleChange}
                  disabled={VulnerabilityExclusionToggleActionLoading}
                />
                <span className="control" />
              </label>
            </OverlayTrigger>
          ) : (
            <label className="toggle-control mb-0">
              <input
                type="checkbox"
                checked={isExclusionEnabled}
                onChange={handleToggleChange}
                disabled={VulnerabilityExclusionToggleActionLoading}
              />
              <span className="control" />
            </label>
          )}
        </div>
      ),
    },
  ];

  const tableData = VulnerabilityExclusionListData?.data || [];
  const totalRecords = VulnerabilityExclusionListData?.totalCount || 0;

  if (VulnerabilityExclusionStatusLoading || (VulnerabilityExclusionListLoading && tableData.length === 0)) {
    return <Spinners />;
  }

  return (
    <CompleteTableData
      isLoading={VulnerabilityExclusionListLoading}
      data={tableData}
      columns={tableColumns}
      TableTitle="Vulnerability Management Exclusion"
      listCount={totalRecords}
      pageLimit={rowsPerPage}
      totalPageCount={calculatePageCount(totalRecords, rowsPerPage)}
      curentPageNumber={currentPage}
      searchKey={searchKeyword}
      moveSearchToEnd={true}
      btnData={actionButtons}
      onPageNoChange={handlePageChange}
      onPageLimitChange={handleLimitChange}
      onSearch={handleSearch}
      onSorting={handleSorting}
      headerSortingState={{
        accessor: sortColumn,
        isDataSorted: sortDirection,
      }}
    />
  );
};

export default memo(VulnerabilityManagementExclusion);